import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { Grid, CnMessage } from '@alife/cn-ui';
import { useMemoizedFn, useMount } from 'ahooks';
import classnames from 'classnames';
import { event } from '@ice/stark-data';
import $i18n from 'panda-i18n';
import { genHabitHandler, genViewSelectorDataSource, getViewDetailFromDataSource, isViewModified, genSearchTags, deleteSearchTags, reorderSearchTags, fetchSuggestions, defaultFetchSuggestions, defaultFetchTransformFields, formatDefaultTransformFieldsApiResponse, reportHottag, formatDefaultTransformSearchParams, } from './utils';
import SearchInput from './components/search-input';
import ViewSelector from './components/view-selector';
import locale from './locales';
import { defaultFieldTransformApi, defaultPresetHabitSetting, defaultSuggestApi, hottagPrefix, } from './const';
import CnOneSearchContext from './context';
import './index.scss';
import FilterTags from './components/filter-tags';
$i18n.addLocale(locale);
const { Row, Col } = Grid;
function CnOneSearch({ className = '', extendButtons = [], onSearch, onClear, disabled = false, isHiddenViewSelector = false, presetHabitSetting = defaultPresetHabitSetting, fetchSuggestApi = '', operatorId = '', baseInfoContext, useDefaultTransformFields = false, defaultSearchTags, fetchSuggest, fieldRender, translateSearchTags, isSuggestPopupViewTargetInputSearch = true, customShowLimitNum, isDisabledSpaceKeyPress, translateResults, components, schema, bizAccessKey = '', isShowFilterTag = true, oneSearchBottomExpend, isEnhancedAnalisys, customHabitHandler, onViewSelectChange, onSearchFormat = (value) => value, isFireSearchWhenDisableEnd = true, loading = false, hideHabitSaveBtn = false, isSelectSuggestAutoSearch = false, isAutoExpand = false, customFilterSlot, onSearchKeyChange, isOpenViewUpdate = false, }, oneSearchRef) {
    // 用新的覆盖旧的
    presetHabitSetting = { ...defaultPresetHabitSetting, ...presetHabitSetting };
    let habitHandler = genHabitHandler(presetHabitSetting.viewConfigStorageKey);
    if (customHabitHandler) {
        habitHandler = customHabitHandler;
    }
    const [filterDataSource, setFilterDataSource] = useState([]);
    const [habitActionId, setHabitActionId] = useState(null);
    const [defaultTranslateResults, setDefaultTranslateResults] = useState();
    const [cacheHabitData, setHabitData] = useState({
        views: [],
        defaultViewId: 'allData',
        needShowUserGuide: false,
    });
    const [keyword, setKeyword] = useState('');
    const [filterSetting, setFilterSetting] = useState({});
    const [filterViewId, setFilterViewId] = useState('');
    const [isCurrentViewModified, setIsCurrentViewModified] = useState(false);
    const [needShowUserGuide, setNeedShowUserGuide] = useState(true);
    const [searchTags, setSearchTags] = useState([]);
    const searchTagsRef = React.useRef(searchTags);
    searchTagsRef.current = searchTags;
    const [translateTags, setTranslateTags] = useState([]);
    const searchStartRef = React.useRef(null);
    useImperativeHandle(oneSearchRef, () => {
        return {
            currentSearchTags: () => {
                return searchTagsRef?.current || searchTags || [];
            },
        };
    });
    const handleSearch = useMemoizedFn((newKeyword, newFilterSetting = {}, newSearchTags = undefined) => {
        let tokenList = Array.isArray(newSearchTags)
            ? newSearchTags
            : searchTags;
        if (newKeyword) {
            tokenList = genSearchTags(tokenList, {
                keyword: newKeyword,
            });
            setSearchTags(tokenList);
        }
        setKeyword('');
        if (newFilterSetting !== filterSetting) {
            setFilterSetting(newFilterSetting);
        }
        if (disabled) {
            return;
        }
        setIsCurrentViewModified(isViewModified(getViewDetailFromDataSource(filterDataSource, filterViewId), newFilterSetting, tokenList));
        const finalTokenList = (tokenList || []).map((item) => {
            const { name, values, labels, analyzeType } = item;
            const token = {
                name,
                values: Array.isArray(labels) && labels.length > 0 ? labels : values,
            };
            if (analyzeType) {
                token.analyzeType = analyzeType;
            }
            return token;
        });
        const reportSearch = () => {
            reportHottag({
                hottag: `${hottagPrefix}.searchInput.onSearch`,
                name: '搜索框-发起搜索',
            });
            if (searchStartRef.current &&
                Date.now() - searchStartRef.current < 5 * 60 * 1000) {
                // 小于5分钟才上报
                reportHottag({
                    hottag: `${hottagPrefix}.searchInput.searchDuration`,
                    name: '搜索框-搜索耗时',
                    c10: Date.now() - searchStartRef.current,
                });
            }
            searchStartRef.current = null;
        };
        // 如果useDefaultTransformFields为true，则使用默认的转换查询条件的字段
        if (useDefaultTransformFields) {
            defaultFetchTransformFields(defaultFieldTransformApi, bizAccessKey, operatorId, finalTokenList, baseInfoContext).then((tokenTranslatorResult) => {
                if (tokenTranslatorResult?.success) {
                    reportSearch();
                    onSearch(onSearchFormat({
                        tokenList: finalTokenList,
                        bizAccessKey,
                        ...formatDefaultTransformSearchParams(tokenTranslatorResult),
                        flag: 'search-flag'
                    }));
                    setDefaultTranslateResults(formatDefaultTransformFieldsApiResponse(tokenTranslatorResult.entityInfos));
                }
                else {
                    setDefaultTranslateResults({ ...defaultTranslateResults });
                    if (tokenTranslatorResult?.errorMsg) {
                        CnMessage.error({
                            title: $i18n.get({
                                id: 'IntelligentParsingFailed',
                                dm: '智能解析失败',
                            }),
                            duration: 5000,
                            content: tokenTranslatorResult.errorMsg,
                        });
                        reportHottag({
                            hottag: `${hottagPrefix}.searchTag.parseFailed`,
                            name: `搜索标签-字段解析失败: ${tokenTranslatorResult.errorMsg}`,
                        });
                    }
                }
            });
        }
        else {
            reportSearch();
            onSearch(onSearchFormat({
                tokenList: finalTokenList,
                bizAccessKey,
                flag: 'search-flag'
            }));
        }
        setTranslateTags([]);
        if (typeof translateSearchTags === 'function') {
            translateSearchTags(finalTokenList).then((newTranslateTags) => {
                setTranslateTags(newTranslateTags);
            });
        }
    });
    const handleKeywordSearch = useMemoizedFn((newKeyword) => {
        handleSearch(newKeyword, filterSetting);
    });
    const handleFetchSuggest = useMemoizedFn((newKeyword) => {
        const appendFakeValue = (suggestList) => {
            if (!Array.isArray(suggestList)) {
                return [];
            }
            return suggestList.map((item, idx) => {
                const { value } = item;
                return {
                    ...item,
                    value: value === undefined || value === null
                        ? `suggest-fake-value-${idx}`
                        : value,
                };
            });
        };
        if (typeof fetchSuggest === 'function') {
            return fetchSuggest(newKeyword, bizAccessKey).then(appendFakeValue);
        }
        else if (fetchSuggestApi) {
            return fetchSuggestions(fetchSuggestApi, newKeyword, bizAccessKey).then(appendFakeValue);
        }
        else {
            return defaultFetchSuggestions(defaultSuggestApi, newKeyword, bizAccessKey, operatorId, baseInfoContext, isEnhancedAnalisys).then(appendFakeValue);
        }
    });
    const handleFilterViewChange = useMemoizedFn((newFilterViewId, dataSource = [], isInit) => {
        setFilterViewId(newFilterViewId);
        const curView = getViewDetailFromDataSource(dataSource, newFilterViewId);
        if (curView) {
            const { filter } = curView;
            const { searchQuery: searchQueryInView = {}, searchTags: searchTagsInView = [], } = filter || {};
            let searchTagsInViewTmp = searchTagsInView;
            if (isInit && curView.type === 'system') {
                // 如果是初始化，则需要将默认的搜索条件和搜索标签加入到搜索条件中
                const defaultSearchTokens = presetHabitSetting?.quickSearchFields?.filter((item) => item.isDefaultShow);
                if (defaultSearchTokens?.length) {
                    searchTagsInViewTmp = searchTagsInViewTmp.concat(defaultSearchTokens.map((it) => ({
                        values: it.values || [],
                        name: it.name,
                        entityKey: it.key,
                        entityType: it.type,
                    })));
                }
                searchTagsInViewTmp = searchTagsInViewTmp.concat(defaultSearchTags || []);
            }
            setSearchTags(searchTagsInViewTmp);
            setIsCurrentViewModified(false);
            if (isEnhancedAnalisys) {
                onViewSelectChange?.(curView);
            }
            else {
                handleSearch('', searchQueryInView, searchTagsInViewTmp);
            }
        }
    });
    const handleHabitDataChange = useMemoizedFn(async (newHabitData, onSaveHook, actionOption = {}) => {
        const confirmCallback = async () => {
            const res = habitActionId
                ? await habitHandler.update(habitActionId, newHabitData, actionOption)
                : await habitHandler.add(newHabitData);
            if (res && res.success) {
                if (!habitActionId) {
                    setHabitActionId(res.data?.index);
                }
                setHabitData(newHabitData);
                const filterViewSelectorDataSource = genViewSelectorDataSource(newHabitData.views || [], presetHabitSetting.defaultViews, $i18n);
                setFilterDataSource(filterViewSelectorDataSource);
                // 设置为默认视图的同时要切换视图
                if (newHabitData.defaultViewId !== cacheHabitData.defaultViewId) {
                    handleFilterViewChange(newHabitData.defaultViewId, filterViewSelectorDataSource);
                }
            }
            if (onSaveHook) {
                onSaveHook(res);
            }
        };
        const cancelCallback = () => onSaveHook({ success: false, isCancel: true });
        if (habitHandler.validate) {
            /**
              将当前 操作类型（方便用户判断什么情况下加自定义操作，否则删除、设置默认等操作都会加上自定义的内容）
              弹窗内容
              确认、取消操作透传出去
            */
            habitHandler.validate(actionOption, confirmCallback, cancelCallback);
            return;
        }
        confirmCallback();
    });
    const handleHideUserGuide = useMemoizedFn(() => {
        setNeedShowUserGuide(false);
        const newHabitData = {
            ...cacheHabitData,
            needShowUserGuide: false,
        };
        handleHabitDataChange(newHabitData, (res) => {
            if (res.success) {
                setHabitData(newHabitData);
            }
        });
    });
    const handleViewIdChange = useMemoizedFn((newViewId) => {
        handleFilterViewChange(newViewId, filterDataSource);
    });
    const handleFocus = useMemoizedFn(() => {
        // 如果searchStartRef.current存在，说明是点击搜索按钮触发的focus
        if (!searchStartRef.current) {
            searchStartRef.current = Date.now();
        }
    });
    const handleClear = useMemoizedFn(() => {
        setSearchTags([]);
        setKeyword('');
        onClear?.();
    });
    const genNewViewDetail = useMemoizedFn(() => {
        return {
            filter: {
                filterSetting,
                searchTags,
            },
        };
    });
    const genFilterViewDetail = useMemoizedFn(() => {
        return {
            searchTags,
        };
    });
    useMount(() => {
        const fetchHabit = async () => {
            const habitRes = await habitHandler.get();
            const { actionId, habitData = {} } = habitRes;
            let currentViewId = presetHabitSetting.defaultViewId || '';
            if (actionId) {
                currentViewId = habitData.defaultViewId || currentViewId;
                habitData.defaultViewId = currentViewId;
                setNeedShowUserGuide('needShowUserGuide' in habitData ? habitData.needShowUserGuide : true);
                setHabitData(habitData);
            }
            else if (currentViewId) {
                setHabitData({
                    ...cacheHabitData,
                    defaultViewId: currentViewId,
                });
            }
            setHabitActionId(actionId);
            const newViewDataSource = genViewSelectorDataSource(habitData.views || [], presetHabitSetting.defaultViews, $i18n);
            setFilterDataSource(newViewDataSource);
            handleFilterViewChange(currentViewId, newViewDataSource, true);
        };
        fetchHabit();
    });
    React.useEffect(() => {
        // 处理插件添加搜索标签的事件
        const onPluginAddSuggestToken = (inputContent) => {
            const params = {};
            if (Object.prototype.toString.call(inputContent) === '[object String]') {
                params.keyword = inputContent;
            }
            else {
                params.suggestItem = inputContent;
            }
            const tokenList = genSearchTags(searchTagsRef?.current || searchTags || [], params);
            searchTagsRef.current = [];
            searchTagsRef.current = tokenList;
            setSearchTags(searchTagsRef.current);
        };
        // 处理插件删除标签的事件
        const onPluginDeleteSuggestToken = (inputContent) => {
            const params = {};
            if (Object.prototype.toString.call(inputContent) === '[object String]') {
                params.keyword = inputContent;
            }
            else {
                params.suggestItem = inputContent;
            }
            const tokenList = deleteSearchTags(searchTagsRef?.current || searchTags || [], params);
            searchTagsRef.current = [];
            searchTagsRef.current = tokenList;
            setSearchTags(searchTagsRef.current);
        };
        // 处理search tag 调整顺序
        const onPluginReorderSuggestToken = (dragTag, dropTag) => {
            const currentSearchTags = searchTagsRef?.current || searchTags || [];
            const params = {};
            if (Object.prototype.toString.call(dragTag) === '[object String]') {
                params.dragTagKeyword = dragTag;
            }
            else {
                params.dragTag = dragTag;
            }
            if (Object.prototype.toString.call(dropTag) === '[object String]') {
                params.dropTagKeyword = dropTag;
            }
            else {
                params.dropTag = dropTag;
            }
            const newOrderSearchTags = reorderSearchTags(currentSearchTags, params);
            searchTagsRef.current = [];
            searchTagsRef.current = newOrderSearchTags;
            setSearchTags(searchTagsRef.current);
        };
        const onPluginClearSuggestToken = () => {
            searchTagsRef.current = [];
            handleClear();
        };
        event.on('cnOneSearchAddSuggestToken', onPluginAddSuggestToken);
        event.on('onPluginDeleteSuggestToken', onPluginDeleteSuggestToken);
        event.on('onPluginReorderSuggestToken', onPluginReorderSuggestToken);
        event.on('onPluginClearSuggestToken', onPluginClearSuggestToken);
        reportHottag({
            hottag: `${hottagPrefix}.searchInput.initiate`,
            name: '搜索框-搜索初始化',
            c4: bizAccessKey || '',
        });
        return () => {
            event.off('cnOneSearchAddSuggestToken', onPluginAddSuggestToken);
            event.off('onPluginDeleteSuggestToken', onPluginDeleteSuggestToken);
            event.off('onPluginReorderSuggestToken', onPluginReorderSuggestToken);
            event.off('onPluginClearSuggestToken', onPluginClearSuggestToken);
        };
    }, []);
    const containerRef = React.useRef(null);
    // 用于处理搜索结果的翻译
    const finalTranslateTags = React.useMemo(() => {
        const transFn = (result) => {
            if (typeof result === 'object' && result) {
                return Object.keys(result).map((key) => {
                    const item = result[key] || {};
                    const entityType = item.entityType || '';
                    const values = item.displayDatabaseValue
                        ? item.entityValues
                        : item.entityChineseValues;
                    return {
                        label: item.entityChineseKey || '',
                        value: Array.isArray(values)
                            ? values.join(entityType === 'RangePickerField' ||
                                entityType === 'RangeDatePickerField'
                                ? ' ~ '
                                : ' , ')
                            : '',
                    };
                });
            }
            return [];
        };
        if (useDefaultTransformFields) {
            return transFn(defaultTranslateResults);
        }
        if (typeof translateResults === 'object' && translateResults) {
            return transFn(translateResults);
        }
        return translateTags;
    }, [
        useDefaultTransformFields,
        translateResults,
        translateTags,
        defaultTranslateResults,
    ]);
    const contextValue = {
        components,
        containerRef,
        schema,
    };
    return (_jsx("div", { className: classnames('cn-one-search', className), ref: containerRef, children: _jsxs(CnOneSearchContext.Provider, { value: contextValue, children: [_jsx(Row, { children: _jsxs(Col, { span: 24, className: 'mod-search', children: [isHiddenViewSelector ? null : (_jsx(ViewSelector, { viewList: filterDataSource, viewId: filterViewId, isOpenViewUpdate: isOpenViewUpdate, onChange: handleViewIdChange, isCurrentViewModified: isCurrentViewModified, needShowUserGuide: needShowUserGuide, onHideUserGuide: handleHideUserGuide, 
                                // 增强分析模式下查询习惯不禁用
                                disabled: isEnhancedAnalisys ? false : disabled, hideHabitSaveBtn: hideHabitSaveBtn, cacheHabitData: cacheHabitData, onHabitDataChange: handleHabitDataChange, presetDefaultViewId: presetHabitSetting.defaultViewId ||
                                    defaultPresetHabitSetting.defaultViewId, genNewViewDetail: genNewViewDetail, defaultViews: presetHabitSetting.defaultViews, extendSaveViewFormSchema: presetHabitSetting.extendSaveViewFormSchema })), _jsx(SearchInput, { searchTags: searchTags, onChangeSearchTags: setSearchTags, onSearch: handleKeywordSearch, keyword: keyword, onChangeSearchWord: (v) => {
                                    setKeyword(v);
                                    onSearchKeyChange && onSearchKeyChange(v);
                                }, onFocus: handleFocus, onClearClick: handleClear, placeholder: presetHabitSetting.searchPlaceholder, disabled: disabled, isFireSearchWhenDisableEnd: isFireSearchWhenDisableEnd, onFetchSuggest: handleFetchSuggest, quickSearchFields: presetHabitSetting.quickSearchFields, quickSearchButtonIcon: presetHabitSetting.quickSearchButtonIcon, isSuggestPopupViewTargetInputSearch: isSuggestPopupViewTargetInputSearch, fieldRender: fieldRender, customShowLimitNum: customShowLimitNum, isDisabledSpaceKeyPress: isDisabledSpaceKeyPress, isEnhancedAnalisys: isEnhancedAnalisys, loading: loading, isSelectSuggestAutoSearch: isSelectSuggestAutoSearch, isAutoExpand: isAutoExpand, customFilterSlot: customFilterSlot }), _jsx("div", { className: 'mod-search-btns', children: extendButtons })] }) }), isShowFilterTag &&
                    Array.isArray(finalTranslateTags) &&
                    finalTranslateTags.length > 0 ? (_jsx(FilterTags, { disabled: disabled, finalTranslateTags: finalTranslateTags, searchTags: searchTags, getNewViewDetail: genFilterViewDetail, baseContext: {
                        bizAccessKey,
                        operatorId,
                    } })) : null, oneSearchBottomExpend || null] }) }));
}
// const CnOneSearch = CnOneSearch;
CnOneSearch.displayName = 'CnOneSearch';
export default forwardRef(CnOneSearch);
